import React, { useState } from "react";
import Axios from "axios";
import { toast } from "sonner";
import { getError } from "../../utils/utils";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Alert } from "../../components/ui/alert";
import { Spinner } from "../../components/ui/spinner";
import { Slider } from "../../components/ui/slider";
import { Calendar } from "../../components/ui/calendar";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogCloseButton,
} from "../../components/ui/dialog";

export default function LoanAgreementModal({
    show,
    onHide,
    refNo,
    totalDebt,
    currency,
    minAmountPercent,
    maxDueDays,
    minOptionsPercent,
    allowedPaymentVariants,
    userToken,
    onSuccess,
}) {
    const [step, setStep] = useState("select");
    const [paymentVariant, setPaymentVariant] = useState(null);
    const [agreedAmount, setAgreedAmount] = useState(0);
    const [amountInputValue, setAmountInputValue] = useState("");
    const [dueDate, setDueDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);

    const effectiveMinOptionsPercent = minOptionsPercent ?? 25;
    const sliderMin = paymentVariant === "options" ? effectiveMinOptionsPercent : 100;
    const sliderMax = 100;
    const minAmount = totalDebt > 0 ? (totalDebt * sliderMin) / 100 : 0;
    const maxAmount = totalDebt;
    const displayPercent = totalDebt > 0 ? (agreedAmount / totalDebt) * 100 : 0;
    const sliderPercent = Math.min(sliderMax, Math.max(sliderMin, Math.round(displayPercent)));
    const currencyLabel = currency === "CZK" ? "Kč" : currency;

    const maxDate = (() => {
        const d = new Date();
        d.setDate(d.getDate() + maxDueDays);
        return d;
    })();

    const minDate = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d;
    })();

    const initAmount = (variant) => {
        const pct = variant === "one" ? 100 : effectiveMinOptionsPercent;
        const amt = totalDebt > 0 ? parseFloat(((totalDebt * pct) / 100).toFixed(2)) : 0;
        setAgreedAmount(amt);
        setAmountInputValue(amt.toFixed(2));
    };

    const handleClose = () => {
        setStep("select");
        setPaymentVariant(null);
        setAgreedAmount(0);
        setAmountInputValue("");
        setDueDate(null);
        setError("");
        setShowConfirm(false);
        onHide();
    };

    const handleSelectVariant = (variant) => {
        setPaymentVariant(variant);
        initAmount(variant);
        setDueDate(null);
        setError("");
        setStep("form");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError("");

        if (!agreedAmount || agreedAmount < minAmount) {
            setError(
                `Zadejte částku alespoň ${sliderMin} % z celkového dluhu (min. ${minAmount.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyLabel}).`
            );
            return;
        }

        if (!dueDate) {
            setError("Zadejte datum splatnosti.");
            return;
        }

        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        const dueDateStr = [
            dueDate.getFullYear(),
            String(dueDate.getMonth() + 1).padStart(2, "0"),
            String(dueDate.getDate()).padStart(2, "0"),
        ].join("-");

        setShowConfirm(false);
        setLoading(true);
        try {
            const { data } = await Axios.post(
                "/api/loan-agreement/submit",
                { refNo, paymentVariant, agreedAmount, dueDate: dueDateStr },
                { headers: { authorization: `Bearer ${userToken}` } }
            );
            toast.success(data.message);
            handleClose();
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(getError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <Dialog open={showConfirm} onOpenChange={(open) => { if (!open) setShowConfirm(false); }}>
            <DialogContent size="sm" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Potvrzení dohody</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <p className="text-sm text-gray-700 mb-6">
                        Klepnutím na tlačítko &ldquo;Potvrzuji&rdquo; stvrzujete uzavření závazné dohody o úhradě Vašeho dluhu. Je nezbytné tuto dohodu řádně dodržet, v opačném případě ponesete důsledky z jejího porušení.
                    </p>
                    <div className="flex gap-2 justify-end">
                        <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={() => setShowConfirm(false)}
                        >
                            Zrušit
                        </Button>
                        <Button
                            type="button"
                            variant="success"
                            onClick={handleConfirm}
                        >
                            Potvrzuji
                        </Button>
                    </div>
                </DialogBody>
            </DialogContent>
        </Dialog>
        <Dialog open={show} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <DialogContent size="md" className="relative" aria-describedby={undefined}>
                <DialogCloseButton />
                <DialogHeader>
                    <DialogTitle>
                        {step === "select"
                            ? "Uzavření dohody"
                            : paymentVariant === "one"
                                ? "Jednorázová úhrada"
                                : "Úhrada dle možností"}
                    </DialogTitle>
                </DialogHeader>
                <DialogBody>
                    {step === "select" && (
                        <div>
                            <p className="text-sm text-gray-600 mb-5">
                                Vyberte způsob, jakým chcete uhradit svůj závazek.
                            </p>
                            <div className="flex flex-col gap-3">
                                {(!allowedPaymentVariants ||
                                    allowedPaymentVariants.includes("one")) && (
                                        <button
                                            type="button"
                                            onClick={() => handleSelectVariant("one")}
                                            className="flex flex-col items-start text-left p-4 rounded-lg border-2 border-[#082359] hover:bg-[#082359]/5 transition-colors"
                                        >
                                            <span className="font-semibold">Úhrada najednou</span>
                                            <span className="text-sm text-gray-500 mt-1">
                                                Zaplatím celou dohodnutou částku v jeden termín
                                            </span>
                                        </button>
                                    )}
                                {(!allowedPaymentVariants ||
                                    allowedPaymentVariants.includes("options")) && (
                                        <button
                                            type="button"
                                            onClick={() => handleSelectVariant("options")}
                                            className="flex flex-col items-start text-left p-4 rounded-lg border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                                        >
                                            <span className="font-semibold">Příslib platby dle možností</span>
                                            <span className="text-sm text-gray-500 mt-1">
                                                Zaplatím tolik, kolik mi aktuální situace umožní
                                            </span>
                                        </button>
                                    )}
                            </div>
                        </div>
                    )}

                    {step === "form" && (
                        <form onSubmit={handleSubmit}>
                            <Alert variant="info" className="text-sm mb-4">
                                Celkový dluh:{" "}
                                <strong>
                                    {totalDebt.toLocaleString("cs-CZ", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}{" "}
                                    {currencyLabel}
                                </strong>
                            </Alert>

                            {error && (
                                <Alert variant="danger" className="mb-4">
                                    {error}
                                </Alert>
                            )}

                            {/* Amount */}
                            <div className="mb-6">
                                <Label className="mb-3 block">
                                    Částka dohody{" "}
                                    <span className="text-red-500">*</span>
                                </Label>
                                {paymentVariant === "one" ? (
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-2xl font-bold text-[#082359]">
                                            100%
                                        </span>
                                        <span className="text-sm text-muted-foreground tabular-nums">
                                            {agreedAmount.toLocaleString("cs-CZ", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}{" "}
                                            {currencyLabel}
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                            <span className="text-2xl font-bold text-[#082359]">
                                                {displayPercent.toFixed(1)}%
                                            </span>
                                            <div className="flex items-center gap-1.5">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={amountInputValue}
                                                    onChange={(e) => setAmountInputValue(e.target.value)}
                                                    onBlur={(e) => {
                                                        const normalized = e.target.value.replace(/\s/g, "").replace(",", ".");
                                                        const raw = parseFloat(normalized);
                                                        if (!isNaN(raw) && totalDebt > 0) {
                                                            const clamped = parseFloat(Math.min(maxAmount, Math.max(totalDebt * sliderMin / 100, raw)).toFixed(2));
                                                            setAgreedAmount(clamped);
                                                            setAmountInputValue(clamped.toFixed(2));
                                                        } else {
                                                            setAmountInputValue(agreedAmount.toFixed(2));
                                                        }
                                                    }}
                                                    className="w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums text-right focus:outline-none focus:ring-2 focus:ring-ring"
                                                />
                                                <span className="text-sm text-muted-foreground">{currencyLabel}</span>
                                            </div>
                                        </div>
                                        <Slider
                                            min={sliderMin}
                                            max={sliderMax}
                                            step={1}
                                            value={[sliderPercent]}
                                            onValueChange={([v]) => {
                                                const amt = parseFloat(((totalDebt * v) / 100).toFixed(2));
                                                setAgreedAmount(amt);
                                                setAmountInputValue(amt.toFixed(2));
                                            }}
                                        />
                                        <div className="flex justify-between mt-1">
                                            <span className="text-xs text-muted-foreground">Min. {sliderMin}%</span>
                                            <span className="text-xs text-muted-foreground">Max. {sliderMax}%</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Due date — calendar */}
                            <div className="mb-4">
                                <Label className="mb-2 block">
                                    Datum splatnosti{" "}
                                    <span className="text-red-500">*</span>
                                </Label>
                                <div className="rounded-lg border border-border overflow-auto w-fit max-w-full">
                                    <Calendar
                                        mode="single"
                                        selected={dueDate}
                                        onSelect={setDueDate}
                                        disabled={(date) =>
                                            date < minDate || date > maxDate
                                        }
                                        initialFocus
                                    />
                                </div>
                                {dueDate && (
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        Zvoleno:{" "}
                                        <strong>
                                            {dueDate.toLocaleDateString("cs-CZ")}
                                        </strong>
                                    </p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Nejpozději {maxDueDays} dní od dnešního dne (do{" "}
                                    {maxDate.toLocaleDateString("cs-CZ")})
                                </p>
                            </div>

                            <div className="flex gap-2 justify-between mt-6">
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    onClick={() => setStep("select")}
                                    disabled={loading}
                                >
                                    Zpět
                                </Button>
                                <Button
                                    type="submit"
                                    variant="success"
                                    disabled={loading}
                                    className="flex items-center gap-2"
                                >
                                    {loading && <Spinner size="sm" />}
                                    {loading
                                        ? "Odesílám…"
                                        : paymentVariant === "one"
                                            ? "Uzavřít dohodu"
                                            : "Přislíbit platbu"}
                                </Button>
                            </div>
                        </form>
                    )}
                </DialogBody>
            </DialogContent>
        </Dialog>
        </>
    );
}
